import React, { useState, useCallback, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import FidelityConnectionModal from '../../features/investments/components/FidelityConnectionModal';

/**
 * PlaidLink Component - Provides UI for connecting bank accounts via Plaid
 * 
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback after successful connection
 * @param {Function} props.onExit - Callback when user exits Plaid Link
 * @param {String} props.connectionType - Type of connection ('banking' or 'liabilities')
 * @param {String} props.buttonText - Custom text for the button
 */
const PlaidLink = ({ 
  onSuccess, 
  onExit, 
  connectionType = 'banking', 
  buttonText = 'Connect Your Bank Account',
  reconnectItemId = null
}) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFidelityModal, setShowFidelityModal] = useState(false);
  const { isAuthenticated } = useAuthContext();

  // Fetch link token on component mount
  useEffect(() => {
    const getLinkToken = async () => {
      if (!isAuthenticated) {
        setError('You must be logged in to connect a bank account');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Use the correct connection type - each type has its own strategy
        const requestData = { 
          connection_type: connectionType,
          ...(reconnectItemId && { item_id: reconnectItemId })
        };
        
        console.log('DEBUG - Requesting link token with connection type:', JSON.stringify(requestData));
        
        const response = await api.post('/api/plaid/create-link-token', requestData);
        
        if (response.data && response.data.linkToken) {
          setLinkToken(response.data.linkToken);
          
          // Check for warning about loan accounts not being available
          if (response.data.warning === 'loan_accounts_not_available' && connectionType === 'liabilities') {
            toast.warning('This bank doesn\'t provide loan account access through our connection. We\'ll connect any available checking, savings, and credit card accounts instead.');
          }
          
          setLoading(false);
        } else {
          throw new Error('No link token returned from server');
        }
      } catch (err) {
        console.error('Error fetching link token:', err);
        
        // Provide detailed error info
        let errorMessage = 'Failed to initialize bank connection. Please try again.';
        
        if (err.response) {
          console.error('Server error response:', err.response.data);
          // If we have a Plaid-specific error, show it
          if (err.response.data && err.response.data.plaid_error) {
            errorMessage = `Plaid error: ${err.response.data.plaid_message || err.response.data.plaid_error}`;
          }
        }
        
        // Try absolute minimal approach as fallback - empty request
        try {
          console.log('Trying absolute minimal fallback - empty request');
          const fallbackResponse = await api.post('/api/plaid/create-link-token', {});
          
          if (fallbackResponse.data && fallbackResponse.data.linkToken) {
            console.log('Fallback approach successful');
            setLinkToken(fallbackResponse.data.linkToken);
            setLoading(false);
            return;
          }
        } catch (fallbackErr) {
          console.error('Fallback link token attempt also failed:', fallbackErr);
        }
        
        // If all attempts fail
        setError(errorMessage);
        setLoading(false);
        toast.error(errorMessage);
      }
    };

    getLinkToken();
  }, [isAuthenticated, connectionType]);

  // Handle successful account linking
  const handleSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        setLoading(true);
        
        // Exchange public token for access token
        // Capture user-selected accounts (default is all accounts selected in Plaid Link)
        const selectedAccountIds = Array.isArray(metadata?.accounts)
          ? metadata.accounts.map(a => a?.id).filter(Boolean)
          : [];

        const response = await api.post('/api/plaid/set-access-token', {
          publicToken,
          metadata,
          selected_account_ids: selectedAccountIds
        });

        // After token exchange, call bulk upsert to materialize only selected accounts
        if (selectedAccountIds && selectedAccountIds.length > 0 && metadata?.institution) {
          try {
            // Use the itemId from the response (Plaid itemId string) or fallback to metadata
            const itemId = response?.data?.item?.id || response?.data?.itemId || metadata?.item_id || metadata?.item?.id;
            
            if (!itemId) {
              console.warn('No itemId available for bulk-upsert, skipping');
            } else {
              console.log('Calling bulk-upsert with itemId:', itemId, 'for', selectedAccountIds.length, 'accounts');
              await api.post('/api/plaid/bulk-upsert', {
                item_id: itemId,
                selected_account_ids: selectedAccountIds
              });
              console.log('Bulk-upsert completed successfully');
            }
          } catch (e) {
            console.error('Bulk upsert failed:', e?.response?.data || e?.message);
            // Don't fail the entire connection if bulk-upsert fails - the accounts might still be synced
            toast.error('Account connection succeeded, but some accounts may need manual sync');
          }
        }
        
        toast.success(`Successfully connected to ${metadata.institution.name}`);

        // Optimistically reflect connection status in localStorage so UI badges show Connected
        try {
          const institutionName = metadata?.institution?.name || 'Bank';
          const existing = JSON.parse(localStorage.getItem('plaidSyncStatus') || '{}');
          existing[institutionName] = { success: true, lastSync: new Date().toISOString(), syncType: 'connect' };
          localStorage.setItem('plaidSyncStatus', JSON.stringify(existing));
          localStorage.setItem('lastPlaidSync', Date.now().toString());
        } catch (e) {
          // noop if storage unavailable
        }
        
        // Call success callback with connection data
        if (onSuccess) {
          onSuccess({
            institutionName: metadata.institution.name,
            accounts: metadata.accounts,
            ...response.data
          });
        }
      } catch (err) {
        console.error('Error setting access token:', err);
        toast.error('Failed to complete bank connection');
        setError('Failed to complete bank connection. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [onSuccess]
  );

  // Configure Plaid Link
  const config = {
    token: linkToken,
    onEvent: (eventName, metadata) => {
      // Detect when Fidelity is selected
      if (eventName === 'SELECT_INSTITUTION' && metadata?.institution) {
        const institutionName = metadata.institution.name || '';
        console.log('Institution selected:', institutionName);
        
        // Check if it's Fidelity
        if (institutionName.toLowerCase().includes('fidelity')) {
          console.log('🏦 Fidelity detected - showing connection options modal');
          setShowFidelityModal(true);
          
          // Close Plaid Link (will trigger onExit but we'll ignore it)
          // The user will use our modal instead
          toast.info('Fidelity requires a different connection method. Choose your preferred option below.');
        }
      }
    },
    onSuccess: (public_token, metadata) => handleSuccess(public_token, metadata),
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid Link exit error:', err);
        
        // Enhanced logging for UWM debugging
        if (metadata && metadata.institution) {
          console.log('DEBUG - Institution details:', {
            name: metadata.institution.name,
            id: metadata.institution.institution_id,
            errorCode: err.error_code,
            errorType: err.error_type,
            errorMessage: err.error_message,
            displayMessage: err.display_message
          });
        }
        
        // Handle specific error types
        if (err.error_code === 'NO_ELIGIBLE_ACCOUNTS') {
          toast.error('No eligible accounts found at this institution. This may be due to account restrictions or temporary connectivity issues. Please try again later or contact support.');
        } else if (err.error_code === 'NO_ACCOUNTS' || err.error_code === 'NO_LIABILITY_ACCOUNTS') {
          console.log('DEBUG - NO_ACCOUNTS error received, attempting universal fallback', {
            errorCode: err.error_code,
            connectionType: connectionType,
            institution: metadata?.institution?.name
          });
          
          // If we got NO_ACCOUNTS with liabilities, try a regular banking connection
          if (connectionType === 'liabilities') {
            console.log('DEBUG - Attempting fallback to regular banking connection');
            
            // Show a more helpful message specific to Plaid Liabilities limitations
            toast.error('This bank doesn\'t provide liability account data (loans/mortgages) through our connection service. We\'ll try to connect your available checking, savings, and credit card accounts instead...', { duration: 6000 });
            
            // Attempt to get a new link token with banking connection type
            setTimeout(async () => {
              try {
                console.log('DEBUG - Requesting fallback link token with banking connection');
                const fallbackResponse = await api.post('/api/plaid/create-link-token', { 
                  connection_type: 'banking' 
                });
                
                if (fallbackResponse.data && fallbackResponse.data.linkToken) {
                  console.log('DEBUG - Fallback link token received, retrying connection');
                  setLinkToken(fallbackResponse.data.linkToken);
                  toast.success('Retrying connection with available account types...');
                } else {
                  toast.error('Unable to establish any connection with this bank. Please try a different institution.');
                }
              } catch (fallbackErr) {
                console.error('DEBUG - Fallback connection also failed:', fallbackErr);
                toast.error('This bank is not compatible with our connection service. Please try adding accounts manually.');
              }
            }, 2000);
          } else {
            // For other connection types, provide helpful guidance
          if (metadata && metadata.institution && 
              (metadata.institution.name.includes('Bank of America') || 
               metadata.institution.name.includes('BofA'))) {
              toast.error('Bank of America limits access to certain account types. Try connecting with a different connection method or bank.');
          } else {
              toast.error('No compatible accounts found at this institution. Please try a different bank or contact support.');
            }
          }
        } else if (err.error_code === 'NO_INVESTMENT_ACCOUNTS') {
          toast.error('We couldn\'t find any investment accounts linked to this institution. Please verify your account has investment features or try connecting a different institution.');
        } else if (err.error_code === 'NO_MORTGAGE_ACCOUNTS' || err.error_code === 'NO_LOAN_ACCOUNTS') {
          toast.error('No supported loan accounts found. Our system can only connect student loans and mortgages - auto loans, personal loans, and other loan types are not supported through this connection method. Please try adding these accounts manually.', { duration: 8000 });
          console.log('DEBUG - No mortgage/loan accounts error:', {
            errorCode: err.error_code,
            connectionType,
            institution: metadata?.institution?.name
          });
        } else if (err.error_code === 'INSTITUTION_DOWN') {
          toast.error('This bank is temporarily unavailable. Please try again in a few minutes.');
        } else if (err.error_code === 'INSTITUTION_NOT_RESPONDING') {
          toast.error('The bank is not responding. Please try again later.');
        } else if (err.error_code === 'INVALID_CREDENTIALS') {
          toast.error('Invalid login credentials. Please check your username and password.');
        } else if (err.error_code === 'INVALID_MFA') {
          toast.error('Invalid security code. Please try again with the correct code.');
        } else if (err.error_code === 'ITEM_LOGIN_REQUIRED') {
          toast.error('Please log in to your bank account and try again.');
        } else if (err.error_code === 'USER_SETUP_REQUIRED') {
          toast.error('Additional setup required at your bank. Please complete any pending setup in your online banking.');
        } else if (err.error_code === 'INSUFFICIENT_CREDENTIALS') {
          toast.error('Additional authentication required. Please ensure you have completed all security steps.');
        } else if (err.error_code === 'INSTITUTION_NOT_SUPPORTED') {
          // Special handling for United Wholesale Mortgage
          if (metadata && metadata.institution && 
              metadata.institution.name.toLowerCase().includes('united wholesale mortgage')) {
            toast.error('United Wholesale Mortgage connectivity issue detected. This institution is supported by Plaid, but may be experiencing temporary issues. Please try: 1) Selecting "United Wholesale Mortgage - Online Payment Login" if available, 2) Trying again in a few minutes, or 3) Using manual entry for now.', { duration: 10000 });
          } else {
            toast.error('This institution is not currently supported. Please try a different bank or use manual entry.');
          }
        } else if (err.error_code === 'CONNECTIVITY_NOT_SUPPORTED') {
          // This is the specific error you're seeing
          if (metadata && metadata.institution && 
              metadata.institution.name.toLowerCase().includes('united wholesale mortgage')) {
            toast.error('United Wholesale Mortgage connection issue: This institution should be supported. Try selecting "United Wholesale Mortgage - Online Payment Login" from the search results, or wait a few minutes and try again. If the issue persists, use manual entry.', { duration: 12000 });
            console.log('DEBUG - UWM Connectivity Error Details:', {
              institutionName: metadata.institution.name,
              institutionId: metadata.institution.institution_id,
              connectionType,
              errorCode: err.error_code
            });
          } else {
            toast.error('This institution does not support the requested connection type. Please try a different connection method or use manual entry.');
          }
        } else if (err.error_message) {
          toast.error(`Connection failed: ${err.error_message}`);
        } else {
          toast.error('Bank connection was cancelled or failed. Please try again.');
        }
        
        // If it's the no liability accounts error, log additional debugging info
        if (err.error_code === 'NO_LIABILITY_ACCOUNTS' && metadata && metadata.institution) {
          console.log('No liability accounts error details:', {
            institution: metadata.institution.name,
            institutionId: metadata.institution.institution_id,
            errorType: err.error_code,
            errorMessage: err.error_message
          });
        }
      }
      if (onExit) {
        onExit(err, metadata);
      }
    },
  };

  const { open, ready } = usePlaidLink(config);

  // Open Plaid Link when component mounts and link token is ready
  useEffect(() => {
    if (ready && linkToken) {
      open();
    }
  }, [ready, linkToken, open]);

  if (loading) {
    return <div className="text-center py-4">Preparing secure connection...</div>;
  }

  if (error) {
    return (
      <div className="text-red-500 text-center py-4">
        <p>{error}</p>
        <button 
          className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => open()}
        disabled={!ready || !linkToken}
        className={`w-full bg-indigo-600 hover:bg-indigo-700 text-gray-100 py-3 px-4 rounded-lg font-medium transition-colors
                  ${(!ready || !linkToken) ? 'opacity-50 cursor-not-allowed' : 'shadow-sm hover:shadow-md'}`}
      >
        {buttonText}
      </button>
      
      {/* Fidelity Connection Options Modal */}
      <FidelityConnectionModal 
        open={showFidelityModal}
        onClose={() => setShowFidelityModal(false)}
      />
    </>
  );
};

export default PlaidLink; 